// Stage-flow derivation for the CI test detail page: turns a CiTestResult
// into an ordered pipeline of stages (start → dump → transfer → restore →
// inference) with per-stage status, duration, and failure detail.
//
// Two data sources, one output shape:
//   1. `stage_events` — authoritative structured events captured by bench's
//      StageTracker (new runs only).
//   2. Field derivation — fallback for historical rows, reconstructed from
//      failure_phase + per-stage timing columns.
import type { CiStageEvent, CiTestResult } from "@/lib/types";

export type FlowStageStatus =
  | "passed"
  | "failed"
  | "warning"
  | "skipped"
  | "not_reached"
  | "unknown";

export interface FlowSubstep {
  label: string;
  status: "passed" | "failed";
  error: string | null;
  kind: "pod" | "node";
}

export interface FlowStage {
  key: string;
  label: string;
  status: FlowStageStatus;
  durationMs: number | null;
  error: string | null;
  detail: string | null;
  classification: string | null;
  substeps: FlowSubstep[];
}

const STAGE_LABELS: Record<string, string> = {
  machine: "Machine",
  start: "Start",
  vanilla_start: "Vanilla start",
  dump: "Checkpoint",
  transfer: "Transfer",
  restore: "Restore",
  inference: "Inference",
};

// Maps the backend failure_phase vocabulary (a superset of the canonical
// stage keys — mirrors failurePhaseToLabels in cryod-timing-waterfall.tsx)
// onto the stage pipeline.
const FAILURE_PHASE_TO_STAGE: Record<string, string> = {
  start: "start",
  dump: "dump",
  // The post-checkpoint source verify (INF-900) fires immediately after the
  // dump completes. Operations that drive the StageTracker publish it as its
  // own `source_inference` event; the ones that don't (sequential_donor_restore)
  // land here, where there is no derived stage of its own — attribute it to
  // dump so the failure renders in the right place instead of leaving every
  // stage "unknown".
  source_inference: "dump",
  transfer: "transfer",
  restore: "restore",
  wake_up: "restore",
  k8s: "restore",
  http_ready: "inference",
  inference: "inference",
  machine: "machine",
};

// Operations whose pipeline includes an explicit transfer stage even when no
// transfer timing was recorded (e.g. the transfer itself failed).
const TRANSFER_OPERATIONS = new Set([
  "cross_restore",
  "cross_pdd",
  "dgd_cross_restore",
  "dgd_cross_pdd",
]);

function titleCase(value: string): string {
  return value
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? titleCase(stage);
}

function makeStage(key: string, status: FlowStageStatus): FlowStage {
  return {
    key,
    label: stageLabel(key),
    status,
    durationMs: null,
    error: null,
    detail: null,
    classification: null,
    substeps: [],
  };
}

function failureHeadline(result: CiTestResult): string | null {
  return result.error_classification_detail ?? result.error ?? null;
}

function podSubsteps(result: CiTestResult): FlowSubstep[] {
  if (!result.restore_pods || result.restore_pods.length === 0) return [];
  return result.restore_pods.map((pod) => ({
    label: `pod ${pod.ordinal + 1}`,
    status: pod.status === "fail" ? "failed" : "passed",
    error: pod.error,
    kind: "pod" as const,
  }));
}

// Same heuristic as the timing waterfall: cold_start_ms alongside any
// source-side or transfer timing means the cold start was a restore
// *fallback*, not the initial engine boot — so it must not be attributed
// to the start stage.
function isFallbackColdStart(result: CiTestResult): boolean {
  const coldStartMs = result.cold_start_ms ?? 0;
  if (coldStartMs <= 0) return false;
  return (
    (result.criu_dump_ms ?? 0) > 0 ||
    (result.mooncake_transfer_ms ?? 0) > 0 ||
    (result.transfer_seconds ?? 0) > 0 ||
    (result.criu_binary_duration_ms ?? 0) > 0 ||
    (result.criu_restore_ms ?? 0) > 0
  );
}

function derivedDurationMs(stage: string, result: CiTestResult): number | null {
  switch (stage) {
    case "start":
      return isFallbackColdStart(result) ? null : (result.cold_start_ms ?? null);
    case "dump":
      return result.criu_dump_ms ?? null;
    case "transfer":
      return (
        result.mooncake_transfer_ms ??
        (result.transfer_seconds != null ? Math.round(result.transfer_seconds * 1000) : null)
      );
    case "restore":
      return result.total_restore_ms ?? result.criu_restore_ms ?? result.criu_binary_duration_ms;
    case "inference":
      return result.inference_ms != null ? Math.round(result.inference_ms) : null;
    default:
      return null;
  }
}

// Pass-path modifiers shared by both data sources: cold-start fallback and
// unverified inference are worth a warning even when the test passed.
function applyWarningModifiers(stages: FlowStage[], result: CiTestResult): void {
  for (const stage of stages) {
    if (stage.key === "restore" && stage.status === "passed") {
      if (result.restore_method === "cold-start") {
        stage.status = "warning";
        stage.detail = "cold-start fallback — CRIU restore did not succeed";
      } else if (result.restore_method === "skipped") {
        stage.status = "skipped";
      }
    }
    if (
      stage.key === "inference" &&
      stage.status === "passed" &&
      result.inference_verified === false
    ) {
      stage.status = "warning";
      stage.detail = "inference not verified";
    }
  }
}

function attachPodSubsteps(stages: FlowStage[], result: CiTestResult): void {
  const substeps = podSubsteps(result);
  if (substeps.length === 0) return;
  const restoreStages = stages.filter((stage) => stage.key === "restore");
  // Scaling ops repeat the restore stage per step — prefer the failed one so
  // the pod chips land where the failure is rendered, else the last.
  const restoreStage =
    restoreStages.find((stage) => stage.status === "failed") ??
    restoreStages[restoreStages.length - 1];
  if (!restoreStage) return;
  // Merge contract: event-provided substeps (multi_restore failed_nodes) are
  // authoritative — don't clobber them with restore_pods if an op ever
  // emits both.
  if (restoreStage.substeps.length > 0) return;
  restoreStage.substeps = substeps;
  const failedPod = substeps.find((substep) => substep.status === "failed");
  if (!failedPod) return;
  if (result.status === "fail") {
    if (restoreStage.status !== "failed") {
      restoreStage.status = "failed";
      restoreStage.error = restoreStage.error ?? failedPod.error ?? failureHeadline(result);
      restoreStage.classification = restoreStage.classification ?? result.error_classification;
    }
  } else if (restoreStage.status === "passed") {
    restoreStage.status = "warning";
    restoreStage.detail = "one or more restore pods failed";
  }
}

function normalizeEventStatus(event: CiStageEvent, result: CiTestResult): FlowStageStatus {
  switch (event.status) {
    case "pass":
      return "passed";
    case "fail":
      return "failed";
    case "skip":
    case "skipped": {
      const reason = event.detail?.reason;
      return reason === "not_reached" ? "not_reached" : "skipped";
    }
    default:
      return result.status === "pass" ? "passed" : "unknown";
  }
}

function eventDurationMs(event: CiStageEvent): number | null {
  if (event.duration_ms != null) return event.duration_ms;
  if (event.started_at && event.completed_at) {
    const startMs = new Date(event.started_at).getTime();
    const endMs = new Date(event.completed_at).getTime();
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs) {
      return endMs - startMs;
    }
  }
  return null;
}

function eventDetailText(event: CiStageEvent): string | null {
  const restoreMethod = event.detail?.restore_method;
  if (restoreMethod === "cold-start") return "cold-start fallback — CRIU restore did not succeed";
  if (event.detail?.expected_failure === true) return "checkpoint failure expected by the test";
  return null;
}

// multi_restore aggregates N sequential legs under one restore stage and
// annotates which ones failed (detail.failed_nodes). Surface them as failed
// substeps so they render as chips next to the stage — the op has no
// restore_pods to fall back on.
function eventFailedNodeSubsteps(event: CiStageEvent): FlowSubstep[] {
  const failedNodes = event.detail?.failed_nodes;
  if (!Array.isArray(failedNodes)) return [];
  return failedNodes
    .filter((node): node is string => typeof node === "string")
    .map((node) => ({ label: node, status: "failed", error: null, kind: "node" as const }));
}

function normalizeStageEvents(events: CiStageEvent[], result: CiTestResult): FlowStage[] {
  const stages = events.map((event) => {
    const stage = makeStage(event.stage, normalizeEventStatus(event, result));
    stage.durationMs = eventDurationMs(event);
    stage.detail = eventDetailText(event);
    stage.substeps = eventFailedNodeSubsteps(event);
    if (stage.status === "failed") {
      stage.error = event.error ?? failureHeadline(result);
      stage.classification = result.error_classification;
    } else if (stage.status === "passed" && event.detail?.restore_method === "cold-start") {
      stage.status = "warning";
    }
    return stage;
  });
  applyWarningModifiers(stages, result);
  attachPodSubsteps(stages, result);
  return stages;
}

function baseStageOrder(result: CiTestResult, failureStage: string | null): string[] {
  if (result.operation === "webhook_test") return ["start"];
  const includeTransfer =
    TRANSFER_OPERATIONS.has(result.operation) ||
    failureStage === "transfer" ||
    (result.mooncake_transfer_ms ?? 0) > 0 ||
    (result.transfer_seconds ?? 0) > 0;
  const order = ["start", "dump"];
  if (includeTransfer) order.push("transfer");
  order.push("restore", "inference");
  return order;
}

function hasDerivableSignal(result: CiTestResult): boolean {
  return (
    result.failure_phase != null ||
    result.restore_method != null ||
    (result.restore_pods?.length ?? 0) > 0 ||
    result.cold_start_ms != null ||
    result.criu_dump_ms != null ||
    result.mooncake_transfer_ms != null ||
    result.transfer_seconds != null ||
    result.total_restore_ms != null ||
    result.criu_restore_ms != null ||
    result.criu_binary_duration_ms != null ||
    result.inference_ms != null
  );
}

function deriveStagesFromFields(result: CiTestResult): FlowStage[] {
  if (!hasDerivableSignal(result)) return [];

  const failureStage =
    result.status === "fail" && result.failure_phase
      ? (FAILURE_PHASE_TO_STAGE[result.failure_phase] ?? null)
      : null;
  const order = baseStageOrder(result, failureStage);

  if (failureStage === "machine") {
    const machineStage = makeStage("machine", "failed");
    machineStage.error = failureHeadline(result);
    machineStage.classification = result.error_classification;
    if (result.machine_name) machineStage.detail = result.machine_name;
    return [machineStage, ...order.map((stage) => makeStage(stage, "not_reached"))];
  }

  const failureIndex = failureStage ? order.indexOf(failureStage) : -1;
  const stages = order.map((stageKey, index) => {
    let status: FlowStageStatus;
    if (result.status === "fail") {
      if (failureIndex === -1) {
        status = "unknown";
      } else if (index < failureIndex) {
        status = "passed";
      } else if (index === failureIndex) {
        status = "failed";
      } else {
        status = "not_reached";
      }
    } else {
      status = "passed";
    }
    const stage = makeStage(stageKey, status);
    stage.durationMs = derivedDurationMs(stageKey, result);
    if (status === "failed") {
      stage.error = failureHeadline(result);
      stage.classification = result.error_classification;
    }
    return stage;
  });

  applyWarningModifiers(stages, result);
  attachPodSubsteps(stages, result);
  return stages;
}

// Vanilla start-attribution probe (bench vanilla_probe.py): after a failed
// Start with an OOM/crash signature, bench reruns the workload once
// unmanaged. Rendered as its own pipeline step right after Start with an
// independent verdict — diagnostic only, never changes the test's overall
// status. Vanilla passing means the failure is attributable to cryod's
// management overhead; vanilla failing (with an OOM/crash of its own) means
// the recipe doesn't fit; inconclusive means the rerun died for a
// non-OOM/crash reason (timeout, infra) and attributes nothing.
function spliceVanillaStartStage(stages: FlowStage[], result: CiTestResult): void {
  const verdict = result.vanilla_start_status;
  if (verdict !== "pass" && verdict !== "fail" && verdict !== "inconclusive") return;
  const startIndex = stages.findIndex((stage) => stage.key === "start");
  if (startIndex === -1) return;
  const stageStatus: FlowStageStatus =
    verdict === "pass" ? "passed" : verdict === "fail" ? "failed" : "warning";
  const vanillaStage = makeStage("vanilla_start", stageStatus);
  vanillaStage.durationMs = result.vanilla_start_duration_ms ?? null;
  if (verdict === "pass") {
    vanillaStage.detail = "unmanaged rerun reached Ready — failure attributed to cryod management";
  } else if (verdict === "fail") {
    vanillaStage.error = result.vanilla_start_error ?? "unmanaged rerun failed";
    vanillaStage.detail = "unmanaged rerun also failed — recipe/sizing does not fit without cryod";
  } else {
    vanillaStage.error = result.vanilla_start_error ?? "unmanaged rerun did not reach Ready";
    vanillaStage.detail =
      "unmanaged rerun failed without an OOM/crash signature — attribution inconclusive";
  }
  stages.splice(startIndex + 1, 0, vanillaStage);
}

export function deriveStageFlow(result: CiTestResult): FlowStage[] {
  if (result.status === "skip") return [];
  const stages =
    result.stage_events && result.stage_events.length > 0
      ? normalizeStageEvents(result.stage_events, result)
      : deriveStagesFromFields(result);
  spliceVanillaStartStage(stages, result);
  return stages;
}
